import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  claimAgentRequest, markAgentRequest, releaseAgentRequest, settleAgentRequest, subscribeAgentRequests,
} from '../agent/requests';
import { consumeAgentNavigation } from '../agent/navigation';
import { agentRuntime } from '../agent/runtime';
import { formatApiResult } from '../../lib/agent/apiResult';

/**
 * Screen-side agent hooks, with Ki_UI's names: how a page answers the agent's
 * `REQUEST_RUN_ANALYSIS` / `REQUEST_*_SUBMIT`, and tells it when a subject it
 * was sent to does not exist.
 */

/**
 * How many mounted handlers each signal has per screen — development only, to
 * catch two components registering for the same one. Only one of them would
 * ever run a given request, and which one would depend on mount order.
 */
const mountedHandlers = new Map();

function registerHandler(id) {
  if (process.env.NODE_ENV === 'production') return () => {};
  const count = (mountedHandlers.get(id) || 0) + 1;
  mountedHandlers.set(id, count);
  if (count > 1) {
    console.warn(
      `[agent] ${count} mounted handlers for ${id}. Only the first to claim a request runs it; `
      + 'register each signal once per screen.',
    );
  }
  return () => {
    const left = (mountedHandlers.get(id) || 1) - 1;
    if (left > 0) mountedHandlers.set(id, left);
    else mountedHandlers.delete(id);
  };
}

/** `useAgentArrival`'s "no URL seen yet on this mount". */
const NO_SUBJECT = Symbol('no subject yet');

const nextTask = () => new Promise((resolve) => { setTimeout(resolve, 0); });

/** The report reason when a confirmation card expired unanswered (the provider's 120 s). */
const CONFIRM_EXPIRED = 'not confirmed within 2 minutes';

const errorText = (err) => {
  if (!err) return 'unknown error';
  if (typeof err === 'string') return err;
  return err.message || String(err);
};

/**
 * What to ask the user before running, or null to run straight away.
 *
 * `confirm` is `true` (ask, with the request's own title), a function returning
 * `false` (do not ask this time) or `{ title, detail, op? }`, or anything falsy.
 * A function lets a screen confirm only the costly case — Studio's risk sweep
 * but not its read-outs — and word the question from its current state.
 */
async function confirmation(confirm, request) {
  if (typeof confirm === 'function') {
    const asked = await confirm({ requestId: request.requestId });
    if (!asked) return null;
    return asked === true ? {} : asked;
  }
  return confirm ? {} : null;
}

/**
 * Answer one signal on one screen. Shared by the two exported hooks.
 *
 * On a pending request for (`signal`, `screenKey`): claim it, then on the next
 * task ask for confirmation if `confirm` says so, then
 * `await handler({ requestId })` and settle with what it returns. The request
 * store sends the one report.
 *
 * The handler starts on the next task rather than inside the claim: the claim
 * can happen during the same commit that mounted the page, and a page that
 * unmounts before then (React StrictMode does, on every mount in development)
 * hands the request back instead of running it from a dead mount. After that,
 * the work is settled even if the page unmounts while it runs: it was started,
 * and the agent is owed its outcome.
 *
 * The subscription is made once per (`signal`, `screenKey`, `enabled`); the
 * latest handler and confirm are read from refs, so closures over fresh render
 * state never re-subscribe. Requests that exist before the page mounts — the
 * usual case, since the agent's navigation is what mounts it — are claimed on
 * mount, and again the moment `enabled` turns true.
 */
function useAgentSignal(screenKey, signal, handler, { enabled = true, confirm = false } = {}) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const confirmRef = useRef(confirm);
  confirmRef.current = confirm;

  const key = screenKey == null ? null : screenKey;

  useEffect(() => {
    if (!enabled || !signal) return undefined;

    let active = true;
    /* Claimed but not started yet: timer handle by request id. */
    const scheduled = new Map();

    const run = (request) => {
      const timer = setTimeout(async () => {
        scheduled.delete(request.requestId);
        let result;
        try {
          const ask = await confirmation(confirmRef.current, request);
          if (ask) {
            markAgentRequest(request.requestId, 'confirm');
            const answer = await agentRuntime().confirm({
              title: ask.title || request.title,
              detail: ask.detail,
            });
            if (answer !== true) {
              /* A card nobody answered is not a card the reader turned down, and
                 the agent should not tell them they cancelled it. */
              settleAgentRequest(request.requestId, {
                ok: false,
                op: ask.op || signal,
                error: answer === 'expired' ? CONFIRM_EXPIRED : 'cancelled by the user',
                cancelled: true,
              });
              return;
            }
            markAgentRequest(request.requestId, 'running');
          }
          const fn = handlerRef.current;
          if (typeof fn !== 'function') throw new Error('this screen has no handler for the request');
          result = await fn({ requestId: request.requestId });
        } catch (err) {
          result = { ok: false, op: signal, error: errorText(err) };
        }
        /* One task for React to commit what the handler just did — a read-out
           that opens its tab and returns at once has not rendered yet — so the
           report goes out with the context of the screen the user now sees. */
        await nextTask();
        settleAgentRequest(request.requestId, result);
      }, 0);
      scheduled.set(request.requestId, timer);
    };

    const claimAll = () => {
      let request = active ? claimAgentRequest(signal, key) : null;
      while (request) {
        run(request);
        request = active ? claimAgentRequest(signal, key) : null;
      }
    };

    const release = registerHandler(`${signal} on ${key ?? 'every screen'}`);
    const unsubscribe = subscribeAgentRequests((event) => {
      if (event?.type === 'pending' && event.request?.type === signal && event.request?.key === key) claimAll();
    });
    claimAll();

    return () => {
      active = false;
      unsubscribe();
      release();
      /* Unmounting before a claimed request started hands it back, so a remount
         or the next handler can still pick it up. */
      scheduled.forEach((timer, requestId) => {
        clearTimeout(timer);
        releaseAgentRequest(requestId);
      });
      scheduled.clear();
    };
  }, [signal, key, enabled]);
}

/**
 * Run this screen's analysis when the agent fires `REQUEST_RUN_ANALYSIS` —
 * the same code its own Run button calls.
 *
 * `handler({ requestId })` reads its inputs from the URL and the `agent` slice,
 * moves the page's own state so the user sees the run, and returns
 * `{ ok: true, op, detail?, data?, queued?, title? }` (data compact: headline
 * numbers, top rows), `{ ok: false, op, error }` or `{ noop: true, op, reason }`.
 * `op` names the operation in the report (`runMfPmReview`). A throw is
 * reported as a failure. Set `confirm` for runs that cost (see `confirmation`).
 */
export function useAgentRunAnalysis(screenKey, handler, options) {
  useAgentSignal(screenKey, 'REQUEST_RUN_ANALYSIS', handler, options);
}

/**
 * Submit this screen's form when the agent fires `signal`
 * (`REQUEST_CLIENT_SUBMIT`, …) — the same code its own submit button calls.
 *
 * Same handler contract as `useAgentRunAnalysis`. `screenKey` null answers the
 * signal on every screen (the watchlist, handled in the shell). Writes should
 * set `confirm`, and reset their form (`resetAgentForm`) after a success.
 */
export function useAgentSubmit(screenKey, signal, handler, options) {
  useAgentSignal(screenKey, signal, handler, options);
}

/**
 * Tell the agent when the subject it sent the user to does not exist.
 *
 * Several screens quietly fall back to another client or fund when the one in
 * the URL is unknown. That is fine for a mistyped link and wrong for the agent,
 * which would go on talking about a subject the page is not showing. When the
 * agent navigated here (the arrival marker, consumed once) and the page then
 * reports `ready && !found`, one `[api_result] navigate failed: <subjectLabel>
 * not found` is sent. A visit the user made by hand never reports.
 *
 * Re-armed per URL, not per mount: moving from one fund to another keeps the
 * same page mounted, and that second arrival must be checked too.
 *
 * `subject`, when given, is the value the page resolves (a query param such as
 * Mandates' `model`). A navigate on the page already open keeps every query
 * param it does not name, so a subject the reader picked by hand rides along
 * when the agent only switches a tab or a filter. Only an arrival that changed
 * it is checked: the agent did not ask for a subject it never sent, and a
 * "navigate failed" for a switch that worked would be a false answer.
 */
export function useAgentArrival(screenKey, {
  ready, found, subjectLabel, subject,
} = {}) {
  const { pathname, search } = useLocation();
  const armed = useRef(false);
  const labelRef = useRef(subjectLabel);
  labelRef.current = subjectLabel;
  const subjectRef = useRef(subject);
  subjectRef.current = subject;
  const tracksSubject = subject !== undefined;
  /* The subject of the URL before this one, on this mount; the sentinel marks a first visit. */
  const lastSubject = useRef(NO_SUBJECT);

  useEffect(() => {
    const before = lastSubject.current;
    const now = subjectRef.current;
    lastSubject.current = now;
    /* Only ever set here, never cleared by a later run: StrictMode runs this
       twice on mount, and the second run finds the marker already consumed by
       the first. */
    if (!screenKey || !consumeAgentNavigation(screenKey)) return;
    const carried = tracksSubject && before !== NO_SUBJECT && String(before ?? '') === String(now ?? '');
    armed.current = !carried;
  }, [screenKey, pathname, search, tracksSubject]);

  useEffect(() => {
    if (!armed.current || !ready) return;
    armed.current = false;
    if (found) return;
    const label = labelRef.current ? String(labelRef.current) : 'the requested item';
    agentRuntime().report(formatApiResult({ op: 'navigate', ok: false, error: `${label} not found` }));
  }, [ready, found, screenKey, pathname, search]);
}
